import React, { useEffect, useState } from "react";
import {
  Briefcase,
  FileText,
  Sparkles,
  Crosshair,
  BookOpen,
  Wrench,
  Film,
  PenLine,
  Lock,
  ArrowUpRight,
  ExternalLink,
  Bell,
  Bookmark,
  ListChecks,
  Map,
  Star,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUnlock } from "@/pages/Dashboard";
import { getMembers, isDemo, getJobCount, trackEvent } from "@/lib/api";
import { demoJobs } from "@/lib/demoData";
import { usePrice } from "@/lib/price";

/**
 * Every dashboard tool. Every entry is REAL — nothing is "Coming soon".
 * `kind`:
 *   - "native"   — full UI lives in the dashboard, calls /api/*
 *   - "embed"    — iframes jobhacki.com/<route>?jhembed=1 (only for tools
 *                  with heavy backend coupling — Resume Builder, Simulator)
 *   - "external" — new tab (custom GPTs etc.)
 */
const TOOLS = [
  { id: "jobs", label: "Job Directory", caption: "Live ATS jobs · direct apply", icon: Briefcase, tier: "free", kind: "native", to: "/dashboard/jobs", color: "bg-[#FFC400]" },
  { id: "playbooks", label: "Playbook Directory", caption: "Grounded money & career playbooks", icon: BookOpen, tier: "free", kind: "native", to: "/dashboard/playbooks", color: "bg-white" },
  { id: "toolbox", label: "Tool Directory", caption: "Hand-picked tools · prompts · repos", icon: Wrench, tier: "free", kind: "native", to: "/dashboard/toolbox", color: "bg-white" },
  { id: "match", label: "Resume Match", caption: "Score your resume vs live roles", icon: Sparkles, tier: "free", kind: "native", to: "/dashboard/match", color: "bg-[#E10600] text-white", badge: "NEW" },
  { id: "resume", label: "Resume Builder", caption: "Beat the bots. Land the interview.", icon: FileText, tier: "paid", kind: "embed", embedSlug: "resume", color: "bg-[#0a0a0a] text-[#FFC400]" },
  { id: "readiness", label: "Job Readiness", caption: "Paste a posting · score yourself", icon: Crosshair, tier: "paid", kind: "native", to: "/dashboard/readiness", color: "bg-[#E10600] text-white" },
  { id: "simulator", label: "Readiness Simulator", caption: "Voice + text role-play, scored.", icon: Crosshair, tier: "paid", kind: "embed", embedSlug: "simulator", color: "bg-[#0a0a0a] text-[#FDFBF7]" },
  { id: "rolefit", label: "RoleFit", caption: "Universal competency scorecard.", icon: Star, tier: "free", kind: "native", to: "/dashboard/rolefit", color: "bg-[#FFC400]" },
  { id: "career-path", label: "Career Path", caption: "Your next role, mapped.", icon: Map, tier: "paid", kind: "native", to: "/dashboard/career-path", color: "bg-[#FFC400]", badge: "NEW" },
  { id: "alerts", label: "Job Alerts", caption: "Bell-track roles + saved searches.", icon: Bell, tier: "free", kind: "native", to: "/dashboard/alerts", color: "bg-[#E10600] text-white" },
  { id: "saved", label: "Saved Jobs", caption: "Your starred roles, synced.", icon: Bookmark, tier: "free", kind: "native", to: "/dashboard/saved", color: "bg-[#FFC400]" },
  { id: "applications", label: "Applications", caption: "Applied → interview → offer.", icon: ListChecks, tier: "free", kind: "native", to: "/dashboard/applications", color: "bg-white" },
  { id: "omnicut", label: "OmniCut", caption: "Frame-by-frame viral edit blueprints", icon: Film, tier: "paid", kind: "external", to: "https://jobhacki.com/omnicut", color: "bg-[#0a0a0a] text-[#FFC400]" },
  { id: "omniscript", label: "OmniScript", caption: "Hook-first AI scripts · waitlist", icon: PenLine, tier: "paid", kind: "external", to: "https://jobhacki.com/omniscript", color: "bg-[#E10600] text-white" },
];

function Tile({ t, paid, openModal }) {
  const Icon = t.icon;
  const locked = t.tier === "paid" && !paid;
  const nav = useNavigate();
  const { price } = usePrice();

  const resolveHref = () => {
    if (t.kind === "embed") return `/dashboard/embed/${t.embedSlug}`;
    return t.to;
  };

  const handleClick = (e) => {
    if (locked) {
      e.preventDefault();
      trackEvent("tool.open.locked", { id: t.id, tier: t.tier });
      openModal(t.label);
      return;
    }
    trackEvent("tool.open", { tool: t.id, kind: t.kind, tier: t.tier });
    if (t.kind === "native" || t.kind === "embed") {
      e.preventDefault();
      nav(resolveHref());
    }
  };

  return (
    <a
      href={locked ? "#" : resolveHref()}
      onClick={handleClick}
      target={!locked && t.kind === "external" ? "_blank" : undefined}
      rel={!locked && t.kind === "external" ? "noopener noreferrer" : undefined}
      data-testid={`tile-${t.id}`}
      data-tier={t.tier}
      data-kind={t.kind}
      className={`${t.color} hover-pop relative border-4 border-[#0a0a0a] shadow-brutal p-5 md:p-6 flex flex-col min-h-[170px] ${
        locked ? "" : "cursor-pointer"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="w-11 h-11 bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] flex items-center justify-center">
          <Icon size={20} strokeWidth={2.5} />
        </div>
        <div className="flex flex-col items-end gap-1">
          {t.badge && (
            <span className="font-accent text-[10px] bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-1.5 py-0.5">
              {t.badge}
            </span>
          )}
          {locked ? (
            <span className="font-accent text-[10px] bg-[#FFC400] text-[#0a0a0a] border-2 border-[#0a0a0a] px-1.5 py-0.5 inline-flex items-center gap-1">
              <Lock size={10} strokeWidth={3} /> {price}
            </span>
          ) : (
            <span className="font-accent text-[10px] bg-[#FDFBF7] text-[#0a0a0a] border-2 border-[#0a0a0a] px-1.5 py-0.5">
              {t.tier === "free" ? "FREE" : "ALL-ACCESS"}
            </span>
          )}
        </div>
      </div>
      <div className="font-display text-xl md:text-2xl leading-tight">{t.label}</div>
      <p className="font-mono-body text-[13px] mt-1.5 opacity-85">{t.caption}</p>
      <div className="mt-auto pt-4 inline-flex items-center gap-1.5 font-accent text-[11px]">
        {locked ? (
          <>
            <Lock size={11} strokeWidth={3} /> Unlock for {price}
          </>
        ) : (
          <>
            Open{" "}
            {t.kind === "external" ? (
              <ExternalLink size={11} strokeWidth={3} />
            ) : (
              <ArrowUpRight size={11} strokeWidth={3} />
            )}
          </>
        )}
      </div>
    </a>
  );
}

export default function DashboardHome() {
  const { open: openModal, paid, me } = useUnlock();
  const { price, remaining, cap } = usePrice();
  const [members, setMembers] = useState(null);
  const [jobsCount, setJobsCount] = useState(null);

  useEffect(() => {
    let alive = true;
    getMembers()
      .then((d) => {
        if (!alive) return;
        const n = d?.count ?? d?.members ?? null;
        if (n != null) setMembers(Number(n).toLocaleString());
      })
      .catch(() => {});
    (async () => {
      // Use the lightweight /api/jobcount endpoint for the hero count.
      // Demo mode keeps the synthetic 338,812 from demoJobs() so the preview
      // looks alive without hitting the gated endpoint.
      if (isDemo()) {
        const res = demoJobs({ page: 1, perPage: 1 });
        if (alive && res?.total != null) {
          setJobsCount(Number(res.total).toLocaleString());
        }
        return;
      }
      const res = await getJobCount().catch(() => null);
      if (!alive || !res) return;
      // Prefer the pre-formatted `display` if backend ships one.
      if (res.display) setJobsCount(String(res.display));
      else if (res.total != null) setJobsCount(Number(res.total).toLocaleString());
    })();
    return () => {
      alive = false;
    };
  }, []);

  const firstName =
    me?.user?.name?.split(" ")[0] ||
    me?.user?.email?.split("@")[0] ||
    me?.email?.split("@")[0] ||
    "operator";

  const groups = [
    { title: "DIRECTORIES", ids: ["jobs", "playbooks", "toolbox"] },
    { title: "READINESS & MATCH", ids: ["match", "readiness", "simulator", "rolefit", "career-path"] },
    { title: "TAILOR YOUR APPLICATION", ids: ["resume"] },
    { title: "TRACKING", ids: ["alerts", "saved", "applications"] },
    { title: "CONTENT", ids: ["omnicut", "omniscript"] },
  ];

  return (
    <div data-testid="dash-home" className="space-y-10">
      <section className="bg-[#0a0a0a] text-[#FDFBF7] border-4 border-[#0a0a0a] shadow-brutal p-6 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="relative">
          <div className="font-accent text-xs bg-[#FFC400] text-[#0a0a0a] inline-block px-2.5 py-1 mb-3">
            {paid ? "ALL-ACCESS · UNLOCKED" : "FREE ACCOUNT"}
          </div>
          <h1
            data-testid="dash-welcome"
            className="font-display text-3xl md:text-5xl leading-[0.95]"
          >
            Welcome back,{" "}
            <span className="bg-[#FFC400] text-[#0a0a0a] px-2">{firstName}</span>.
          </h1>
          <p className="font-mono-body text-sm md:text-base mt-3 text-[#FDFBF7]/80 max-w-2xl">
            {paid
              ? "Every tool unlocked. Build the resume, score the role, ship the content."
              : `Use the free tools — Directories, Resume Match, RoleFit — and unlock the rest for ${price} one-time.`}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 font-mono-body text-xs text-[#FDFBF7]/70">
            {jobsCount && (
              <span className="inline-flex items-center gap-2" data-testid="hero-jobs-count">
                <span className="live-dot" />
                <b className="font-display text-[#FFC400]">{jobsCount}</b> live ATS jobs
              </span>
            )}
            {members && (
              <span className="inline-flex items-center gap-2" data-testid="hero-members-count">
                <b className="font-display text-[#FFC400]">{members}</b> operators building right now
              </span>
            )}
            {!paid && remaining && cap && (
              <span
                className="inline-flex items-center gap-2"
                data-testid="hero-price-scarcity"
              >
                <b className="font-display text-[#FFC400]">
                  {remaining.toLocaleString()}
                </b>{" "}
                of {cap.toLocaleString()} launch spots left at {price}
              </span>
            )}
          </div>
        </div>
      </section>

      {groups.map((g) => {
        const items = g.ids
          .map((id) => TOOLS.find((t) => t.id === id))
          .filter(Boolean);
        return (
          <section key={g.title}>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-xl md:text-2xl">{g.title}</h2>
              <span className="font-mono-body text-xs text-[#0a0a0a]/55">
                {items.filter((t) => t.tier === "free" || paid).length} unlocked
              </span>
            </div>
            <div
              data-testid={`dash-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            >
              {items.map((t) => (
                <Tile key={t.id} t={t} paid={paid} openModal={openModal} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
